import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { PageShell } from "@/components/PageShell";
import { SeoHead } from "@/components/SeoHead";
import heroImg from "@/assets/hero-strategy.webp";
import heroImgMd from "@/assets/hero-strategy-md.webp";
import heroImgSm from "@/assets/hero-strategy-sm.webp";

const HomeBelowFold = lazy(() => import("@/components/HomeBelowFold"));

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Comm42 SA — Turning strategy into measurable performance" },
      { name: "description", content: "Strategic consulting, legal determination, investigation, and international logistics. Worldwide partner for individuals, companies and organizations." },
      { property: "og:title", content: "Comm42 SA — Strategy that performs, worldwide" },
      { property: "og:description", content: "Investigate · Protect · Support. We build the right strategy to achieve your goals." },
      { property: "og:url", content: "https://comm42.ch/" },
      { property: "og:type", content: "website" },
    ],
    links: [
      { rel: "canonical", href: "https://comm42.ch/" },
      { rel: "preload", as: "image", href: heroImgSm, imageSrcSet: `${heroImgSm} 800w, ${heroImgMd} 1280w, ${heroImg} 1920w`, imageSizes: "100vw", fetchPriority: "high" } as any,
    ],
  }),
  component: Index,
});

function Index() {
  const { t } = useTranslation();
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [showBelowFold, setShowBelowFold] = useState(false);

  useEffect(() => {
    // Idle-load fallback so it eventually mounts even without scroll
    const idle = (window as any).requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 1500));
    const idleId = idle(() => setShowBelowFold(true));

    if (!sentinelRef.current) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShowBelowFold(true);
          io.disconnect();
        }
      },
      { rootMargin: "600px 0px" }
    );
    io.observe(sentinelRef.current);
    return () => {
      io.disconnect();
      if ((window as any).cancelIdleCallback) (window as any).cancelIdleCallback(idleId);
    };
  }, []);

  return (
    <PageShell>
      <SeoHead page="home" />
      {/* Hero */}
      <section className="relative flex min-h-screen items-center overflow-hidden perspective-1000">
        <img
          src={heroImg}
          srcSet={`${heroImgSm} 800w, ${heroImgMd} 1280w, ${heroImg} 1920w`}
          sizes="100vw"
          alt=""
          width={1920}
          height={1280}
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 h-full w-full scale-110 object-cover animate-float"
        />
        <div className="absolute inset-0 bg-background/30" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/20 to-background" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/60" />
        <div className="absolute inset-0 opacity-60 [background-image:radial-gradient(ellipse_at_center,transparent_30%,oklch(0.15_0.04_240/0.85)_90%)]" />
        <div className="absolute inset-0 opacity-40 [background-image:radial-gradient(circle_at_15%_50%,oklch(0.5_0.09_235/0.35),transparent_55%),radial-gradient(circle_at_85%_30%,oklch(0.85_0.12_85/0.15),transparent_55%)]" />
        <div className="relative z-10 mx-auto max-w-5xl px-6 text-center">
          <p className="reveal-up text-xs tracking-[0.5em] text-primary">{t("home.welcome")}</p>
          <h1 className="mt-8 font-serif text-5xl leading-tight md:text-7xl">
            {t("home.titlePart1")} <em className="text-shimmer not-italic">{t("home.titleEm")}</em> {t("home.titlePart2")}
          </h1>
          <p className="reveal-up mx-auto mt-6 max-w-2xl text-lg text-muted-foreground" style={{ animationDelay: "0.4s" }}>
            {t("home.subtitle")}
          </p>
          <div className="reveal-up mt-10 flex flex-wrap items-center justify-center gap-4" style={{ animationDelay: "0.55s" }}>
            <Link
              to="/contact"
              className="group relative inline-flex items-center gap-2 overflow-hidden border border-primary px-8 py-3 text-xs tracking-[0.3em] text-primary transition-all hover:bg-primary hover:text-primary-foreground hover:shadow-[0_20px_40px_-10px_oklch(0.5_0.09_235/0.45)]"
            >
              <span className="relative z-10">{t("home.getInfo")}</span>
              <ArrowRight className="relative z-10 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/services" className="link-magnetic text-xs tracking-[0.3em] text-foreground/80 hover:text-primary">
              {t("home.exploreServices")}
            </Link>
          </div>
        </div>
      </section>

      <div ref={sentinelRef} aria-hidden="true" />
      {showBelowFold && (
        <Suspense fallback={null}>
          <HomeBelowFold />
        </Suspense>
      )}
    </PageShell>
  );
}
